/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Texture, Rectangle } from 'pixi.js-legacy';
import { ComponentInterface } from '../../canvas-common/interfaces/component.interface';
import { TicketContainer } from '@appPlanApplication/tickets/TicketContainer';
import { SpriteTicket } from '@appPlanApplication/tickets/SpriteTicket';
import { ticketSchema } from '../ticket-components/TicketSchema';
import { CanvasAngularLand } from 'src/app/app-data/interfaces/canvas-angular-land';
import { TicketType } from '@touchplan/common';
import { setTicketWidthHelper } from 'src/app/utils/ticket-utils';

/**
 * This class is the main representation of a ticket. It has a simple component api to add individual pieces associated with the same data
 */
export class SpriteTask extends SpriteTicket implements ComponentInterface {
	public override type: string = TicketType.Task;
	private canvasAngularLand: CanvasAngularLand;
	/**
	 * @param {PIXI.Texture} ticketShape
	 * @param {PlanState} planState
	 * @param {TicketContainer} ticketContainer
	 */
	constructor(ticketShape: Texture, canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		super(ticketShape, canvasAngularLand, ticketContainer);
		this.canvasAngularLand = canvasAngularLand;

		const data = ticketContainer.getData();

		if (data.isGhost) {
			ticketContainer.components.addDynamicComponents(
				ticketSchema.createGhostTaskSchema(canvasAngularLand, ticketContainer)
			);
		} else {
			ticketContainer.components.addDynamicComponents(
				ticketSchema.createTaskSchema(canvasAngularLand, ticketContainer)
			);
		}
	}

	private updateHitArea(width: number, height: number): void {
		if (this.ticketContainer.SURFACE.hitArea && this.ticketContainer.SURFACE.hitArea instanceof Rectangle) {
			this.ticketContainer.SURFACE.hitArea.width = width;
			this.ticketContainer.SURFACE.hitArea.height = height;
		} else {
			this.ticketContainer.SURFACE.hitArea = new Rectangle(0, 0, width, height);
		}
	}

	public override update(
		canvasAngularLand: CanvasAngularLand,
		ticketContainer: TicketContainer,
		forceDirty: boolean = false
	): void {
		super.update(canvasAngularLand, ticketContainer, forceDirty);

		const data = ticketContainer.getData();
		const layout = ticketContainer.getLayout();

		if (ticketContainer.visible && ticketContainer.renderable) {
			this.width = setTicketWidthHelper(
				data,
				layout,
				this.canvasAngularLand.wideTicketSize$.getValue() as number
			);
			this.height = layout.height;
			this.updateHitArea(this.width, layout.height);
		}
	}

	public override updateData(canvasAngularLand: CanvasAngularLand, ticketContainer: TicketContainer) {
		super.updateData(canvasAngularLand, ticketContainer);

		const data = ticketContainer.getData();
		const layout = ticketContainer.getLayout();

		this.width = setTicketWidthHelper(
			data,
			layout,
			this.canvasAngularLand.wideTicketSize$.getValue() as number
		);
		this.height = layout.height;
		this.updateHitArea(this.width, layout.height);
	}
}
